import {
  Box,
  Button,
  Checkbox,
  Stack,
  HStack,
  VStack,
  Text,
  Avatar,
  InputGroup,
  InputRightElement,
  Input,
  Icon,
  Wrap,
  WrapItem,
  Collapse,
  Flex,
  InputLeftAddon,
  Image,
  useDisclosure,
  Heading,
  Textarea,
  Spinner,
  Tabs,
  TabList,
  Tab,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  AiOutlineEyeInvisible,
} from 'react-icons/ai';
import { FiEdit2 } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../../../components/header/Header';
import { CustomToast } from '../../../components/toast/CustomToast';
import { mutateFunction, useMutation } from '../../../libs/apis';
import IsDesktop from '../../../utils/IsDesktop';
import { accountSettingsSchema } from '../../../validators';
import BrandButton from '../../../components/brandButton/BrandButton';
import lock from '../../../public/icons/lock.svg';
import { setUserInfo } from '../../../redux-toolkit/reducers/userSlice';
import UserService from '../../../utils/UserService';
import { setCompanyInfo } from '../../../redux-toolkit/reducers/companySlice';
import { setExpertInfo } from '../../../redux-toolkit/reducers/expertSlice';
import { setCompleteSurvey } from '../../../redux-toolkit/reducers/SurveySlice';
import MySlots from '../../experts/Settings/mySlots/MySlots';
import EditIcon from '../../../public/icons_v2/edit.svg';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DeleteAccountModal from '../../../components/modal/DeleteAccountModal';
import { useData } from '../../../data';
import { AddIcon, StarIcon } from '@chakra-ui/icons';

import evaluateCompanyProfile from '../../../utils/evaluateCompanyProfile';
import { useClickOutside } from '../../../utils/useClickOutside';
import { setAllCompaniesInfo } from '../../../redux-toolkit/reducers/allCompaniesSlice';
import CustomerSupportModal from '../../../components/modal/CustomerSupportModal';
import { FaImage, FaTimes, FaUserAlt, FaVideo } from 'react-icons/fa';
import InviteUserModal from '../../../components/modal/InviteUserModal';
import { useQueryClient } from 'react-query';
import Loader from '../../../components/dealFlow/Loader';
import EmbedVideoLinkModal from '../../../components/modal/EmbedVideoLinkModal';
import MediaDisplay from '../../../components/feed/MediaDisplay';
import CompanyProile from './CompanyProfile';

const AccountSettings = () => {
  const navigate = useNavigate();
  const {
    isOpen: deleteAccountModalIsOpen,
    onOpen: onOpenDeleteAccountModal,
    onClose: closeDeleteAccountModal,
  } = useDisclosure();
  const {
    isOpen: shareInviteModalIsOpen,
    onOpen: onOpenShareInviteModal,
    onClose: onCloseShareInviteModal,
  } = useDisclosure();
  const {
    isOpen: submitComplaintModalIsOpen,
    onOpen: onOpenSubmitComplaintModal,
    onClose: onCloseSubmitComplaintModal,
  } = useDisclosure();
  const {
    isOpen: addCompanyModalIsOpen,
    onOpen: onOpenAddCompanyModal,
    onClose: onCloseAddCompanyModal,
  } = useDisclosure();

  const queryClient = useQueryClient();
  const isDesktop = IsDesktop();
  const { addToast } = CustomToast();
  const loggedInUser = useSelector((state) => state?.user?.userInfo);
  const { mutate, isLoading } = useMutation(mutateFunction);
  const { mutate: mutateUpload, isLoading: isLoadingUpload } =
    useMutation(mutateFunction);
  const {
    mutate: mutateProfileImageUpload,
    isLoading: isLoadingProfileImageUpload,
  } = useMutation(mutateFunction);
  const { mutate: mutateAddExtraCompany, isLoading: isLoadingAddExtraCompany } =
    useMutation(mutateFunction);
  const { mutate: mutateImageUpload, isLoading: isLoadingImageUpload } =
    useMutation(mutateFunction);
  const userDetails = useSelector((state) => state?.user?.userInfo);
  const {
    data,
    refetch,
    isLoading: isLoadingUserDetails,
    isError: isUserDetailsError,
  } = useData(`users/${userDetails.id}`);

  const { data: _surveydata, isLoading: surveyDataIsLoading } =
    useData('survey');
  const surveyData = _surveydata?.data;
  const [searchParams, setSearchParams] = useSearchParams();
  let location = useLocation();
  const topRef = useRef();
  const pitchDeckRef = useRef();
  const videoUrlRef = useRef();
  const companyStageRef = useRef();
  const incorporationLocationRef = useRef();
  const businessModelRef = useRef();
  const businessCategoryRef = useRef();
  const fundCategoryRef = useRef();
  const problemRef = useRef();
  const solutionRef = useRef();
  const marketGrowthRef = useRef();
  const revenueModelRef = useRef();
  const competitionRef = useRef();
  const fundsUseRef = useRef();
  const stakeholdersRef = useRef();
  const customerTypeRef = useRef();
  const expertCompanyRef = useRef();
  const expertCountryRef = useRef();
  const expertCategoryRef = useRef();
  const expertBioRef = useRef();
  const expertHeadlineRef = useRef();
  const expertPhoneRef = useRef();

  const [tabIndex, setTabIndex] = useState(1);

  const refs = {
    pitchDeckRef,
    videoUrlRef,
    companyStageRef,
    incorporationLocationRef,
    businessModelRef,
    businessCategoryRef,
    fundCategoryRef,
    problemRef,
    solutionRef,
    marketGrowthRef,
    revenueModelRef,
    competitionRef,
    fundsUseRef,
    stakeholdersRef,
    customerTypeRef,
    expertCompanyRef,
    expertCountryRef,
    expertCategoryRef,
    expertBioRef,
    expertHeadlineRef,
    expertPhoneRef,
  };

  const toastAlerts = {
    pitchDeck: 'Add a pitch deck to increase visibility',
    videoUrl: 'Add an explainer video to increase visibility',
    incorporationLocation: `Select your company's incorporation locations to improve company profile`,
    businessCategory: `Select your company's business category to improve company profile`,
    businessModel: `Select your company's business model to improve company profile`,
    fundCategory: `Select your company's fund category to improve company profile`,
    companyStage: `Select your company's stage to improve company profile`,
    problem: 'Add a problem statement to improve company profile',
    solution: 'Add a solution statement to improve company profile',
    marketGrowth: 'Add market penetration brief to improve company profile',
    revenueModel: 'Add a revenue strategy brief to improve company profile',
    competition: 'List potential competitors to improve company profile',
    fundsUse: 'Add fund allocation strategy to improve company profile',
    stakeholders: 'Add a list of current stakeholders',
    customerType: 'Add a list of targeted customer bases',
    expertCompany: 'Add name of your current company',
    expertCountry: 'Select the country you currently work from',
    expertCategory: 'Select your job category',
    expertHeadline: 'Add your positon',
    expertPhone: 'Add your phone number',
    expertBio: 'Please add a brief bio',
  };

  const [loading, setLoading] = useState(false);

  const [userDetailsData, setUserDetailsData] = useState({
    first_name: data?.data?.user?.first_name,
    last_name: data?.data?.user?.last_name,
    email: data?.data?.user?.email,
    profile_image: data?.data?.user?.profile_image,
    phone_number: data?.data?.user?.phone_number,
  });

  const [pushNotf, setPushNotf] = useState(data?.data?.user?.push_notification);
  const [emailNotf, setEmailNotf] = useState(
    data?.data?.user?.email_notification,
  );
  const [currentCompanyIndex, setCurrentCompanyIndex] = useState('0');
  const [inboxNotification, setInboxNotification] = useState(
    data?.data?.user?.systemPreferences?.inboxNotification || false,
  );
  const [friendRequestNotification, setFriendRequestNotification] = useState(
    data?.data?.user?.systemPreferences?.friendRequestNotification || false,
  );
  const [promoNotf, setPromoNotf] = useState(
    data?.data?.user?.promotional_newsletter,
  );

  useEffect(() => {
    setPushNotf(data?.data?.user?.push_notification);
    setEmailNotf(data?.data?.user?.email_notification);
    setPromoNotf(data?.data?.user?.promotional_newsletter);
    setFriendRequestNotification(
      data?.data?.user?.systemPreferences?.friendRequestNotification,
    );
    setInboxNotification(
      data?.data?.user?.systemPreferences?.inboxNotification,
    );
  }, [
    data?.data?.user?.email_notification,
    data?.data?.user?.promotional_newsletter,
    data?.data?.user?.push_notification,
    data?.data?.user?.systemPreferences?.inboxNotification,
    data?.data?.user?.systemPreferences?.friendRequestNotification,
  ]);

  useEffect(() => {
    setUserDetailsData({
      first_name: data?.data?.user?.first_name,
      last_name: data?.data?.user?.last_name,
      email: data?.data?.user?.email,
      profile_image: data?.data?.user?.profile_image,
      phone_number: data?.data?.user?.phone_number,
    });
  }, [data?.data?.user]);

  const [profilePicture, setProfilePicture] = useState(
    data?.data?.user?.cover_image || userDetails?.profile_image,
  );
  const companyDetails = useSelector((state) => state?.company?.companyInfo);
  const expertDetails = useSelector((state) => state?.expert?.expertInfo);

  const viewProfileAsOthers = () => {
    try {
      navigate(
        `/view-user?userid=${loggedInUser?.id}&usertype=${loggedInUser?.userTypeId}${companyDetails?.id ? `&cid=${companyDetails?.id}` : ''}`,
      );
    } catch (error) { }
  };
  const accountType = useSelector(
    (state) => state?.user?.userInfo?.userTypeTitle,
  );
  const shouldGoToEditSurvey = useSelector(
    (state) => state?.survey?.completeSurvey,
  );
  const dispatch = useDispatch();
  const [showPasswowordReset, setShowPasswordReset] = useState(false);
  const [password, setPassword] = useState({});
  const {
    isOpen: isOpenEditSurvey,
    onToggle: onToggleEditSurvey,
    onOpen: onOpenEditSurvey,
    onClose: onCloseEditSurvey,
  } = useDisclosure();
  const {
    isOpen: isOpenEditExpert,
    onToggle: onToggleEditExpert,
    onOpen: onOpenEditExpert,
    onClose: onCloseEditExpert,
  } = useDisclosure();

  const safeValue = (val, fallback = '') =>
    val === null || val === 'null' || val === undefined ? fallback : val;

  const [filledData, setFilledData] = useState({});

  const prevCompanyId = useRef();

  useEffect(() => {
    if (companyDetails?.id !== prevCompanyId.current) {
      setFilledData({
        title: safeValue(companyDetails?.title),
        job_title: safeValue(companyDetails?.job_title),
        description: safeValue(companyDetails?.description),
        website_url: safeValue(companyDetails?.website_url),
        social_media_url: safeValue(companyDetails?.social_media_url),
        pitch_deck: safeValue(companyDetails?.pitch_deck),
        video_url: safeValue(companyDetails?.video_url),
        businessCategoryId: safeValue(
          companyDetails?.business_category_ids,
          [],
        ),
        businessModelId: safeValue(companyDetails?.business_model_ids, []),
        fundCategoryId: safeValue(companyDetails?.fundCategoryId),
        incorporationLocationId: safeValue(
          companyDetails?.incorporation_location_ids,
          [],
        ),
        investmentlocationId: safeValue(
          companyDetails?.investment_location_ids,
          [],
        ),
        companyStageId: safeValue(companyDetails?.company_stage_ids, []),
        problem: safeValue(companyDetails?.problem),
        solution: safeValue(companyDetails?.solution),
        market_growth: safeValue(companyDetails?.market_growth),
        revenue_model: safeValue(companyDetails?.revenue_model),
        competition: safeValue(companyDetails?.competition),
        fund_use: safeValue(companyDetails?.fund_use),
        stakeholders: safeValue(companyDetails?.stakeholders),
        customer_type: safeValue(companyDetails?.customer_type),
        valuation: safeValue(companyDetails?.valuation),
        number_of_investors: safeValue(companyDetails?.number_of_investors),
        funding_target: safeValue(companyDetails?.funding_target),
      });

      prevCompanyId.current = companyDetails?.id;
    }
  }, [companyDetails]);

  const [videoLink, setVideoLink] = useState('');

  const allCompaniesInfo = useSelector(
    (state) => state?.allcompanies?.allCompaniesInfo || [],
  );
  const [expertFilledData, setExpertFilledData] = useState({
    company_name: expertDetails?.company_name,
    headline: userDetails?.headline,
    bio: userDetails?.bio,
    phone_number: userDetails?.phone_number,
  });
  const { data: expertCategoriesData } = useData('experts/categories');
  const { data: expertLocations } = useData('experts/locations');
  const list = expertCategoriesData?.data?.categories || [];
  const countriesList = expertLocations?.data?.locations || [];
  const [selected, setSelected] = useState(
    expertDetails?.expert_category || null,
  );
  const [searchValue, setSearchValue] = useState(
    expertDetails?.expert_category?.title || '',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(
    expertDetails?.location || null,
  );
  const [searchLocationValue, setSearchLocationValue] = useState(
    expertDetails?.location?.title || '',
  );
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [ref] = useClickOutside(() => {
    if (isOpen) setIsOpen(false);
  });
  const [images, setImages] = useState(null);
  const [imageSource, setImageSource] = useState([]);
  const imagesInputRef = useRef(null);
  const [imageIndex, setImageIndex] = useState();
  const [countrySelectionError, setCountrySelectionError] = useState(false);
  const maxBytes = 10485760;
  const maxSteps = 6;
  const [addCompanyData, setAddCompanyData] = useState({
    title: '',
    job_title: '',
    description: '',
    website_url: '',
    social_media_url: '',
    pitch_deck: '',
    video_url: '',
    businessCategoryId: null,
    businessModelId: null,
    fundCategoryId: null,
    incorporationLocationId: null,
    investmentlocationId: null,
    companyStageId: null,
    number_of_investors: 0,
    funding_target: 0,
    valuation: 0,
    problem: '',
    solution: '',
    market_growth: '',
    revenue_model: '',
    competition: '',
    fund_use: '',
    stakeholders: '',
    customer_type: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [locationRef] = useClickOutside(() => {
    if (isLocationOpen) setIsLocationOpen(false);
  });

  const loggedInUserSubscription = useSelector(
    (state) => state?.user?.userSubscriptionInfo,
  );
  const changeDPRef = useRef(null);
  const changeBannerRef = useRef(null);
  const {
    isOpen: createEmbedVideoIsOpen,
    onClose: onCloseCreateEmbedVideo,
    onOpen: onOpenCreateEmbedVideo,
  } = useDisclosure();
  useEffect(() => {
    let index = allCompaniesInfo.findIndex(
      ({ id }) => id === companyDetails?.id,
    );
    index >= 0 && setCurrentCompanyIndex(`${index}`);
  }, [companyDetails?.title]);

  const searchCategoriesFilter = (value) => {
    return searchValue
      ? value?.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
      value?.title == 'Other'
      : true;
  };
  const searchLocationFilter = (value) => {
    return searchLocationValue
      ? value?.title
        ?.toLowerCase()
        .includes(searchLocationValue.toLowerCase()) ||
      value?.title == 'Other'
      : true;
  };
  const onChange = (e) => {
    setIsOpen(true);
    setSearchValue(e.target.value);
    setSelected(null);
  };

  const onChangeLocation = (e) => {
    setIsLocationOpen(true);
    setSearchLocationValue(e.target.value);
    setSelectedLocation(null);
    setCountrySelectionError(false);
  };

  const selectItem = (item) => {
    setSearchValue(item?.title);
    setSelected(item);
    toggleDropDown();
  };

  const selectLocation = (item) => {
    setSearchLocationValue(item?.title);
    setSelectedLocation(item);
    toggleLocationDropDown();
  };

  const toggleDropDown = () => setIsOpen((prev) => !prev);
  const toggleLocationDropDown = () => setIsLocationOpen((prev) => !prev);

  const openImageUpload = (e) => {
    imagesInputRef.current.click();
  };

  const handleChangeForImages = (e) => {
    let isTooLarge = [...e.target.files].some((file) => file.size > maxBytes);
    if (isTooLarge) {
      setTimeout(() => {
        addToast({
          message: 'One or more files are greater than 10MB',
          type: 'warning',
        });
      }, 500);
      return;
    }
    let remainingSlots =
      8 -
      (accountType === 'founder'
        ? companyDetails?.additional_images?.length
        : expertDetails?.additional_images?.length) || 0;
    if (e.target.files.length <= remainingSlots) {
      const files = e.target.files;
      const imagesArray = [];
      setImages(e.target.files);
      Array.from(files).forEach((file) => {
        const reader = new FileReader();

        reader.onload = function (e) {
          imagesArray.push(e.target.result);
          if (imagesArray.length === files.length) {
            setImageSource(imagesArray);
          }
        };

        reader.readAsDataURL(file);
      });
    } else {
      setTimeout(() => {
        addToast({
          message: `More than ${remainingSlots} files selected`,
          type: 'warning',
        });
      }, 500);
    }
  };

  const uploadExtraImages = () => {
    if (isLoadingImageUpload) return;
    const attachments = [...images];
    let formData = new FormData();
    for (let i = 0; i < attachments.length; i++) {
      formData.append('attachments', attachments[i]);
    }
    mutateImageUpload(
      {
        key:
          accountType === 'founder'
            ? `companies/addExtraImages/${companyDetails?.id}`
            : `experts/addExtraImages`,
        method: 'patch',
        data: formData,
      },
      {
        onSuccess(res) {
          addToast(res);
          if (accountType === 'founder') {
            dispatch(
              setCompanyInfo({
                ...companyDetails,
                additional_images: [...res.data?.additional_images],
              }),
            );
            UserService.setCompanyInfo({
              ...companyDetails,
              additional_images: [...res.data?.additional_images],
            });
            UserService.setAllCompaniesInfo(
              [...allCompaniesInfo].map((_company) =>
                _company.id === companyDetails?.id
                  ? {
                    ...companyDetails,
                    additional_images: [...res.data?.additional_images],
                  }
                  : _company,
              ),
            );
            dispatch(
              setAllCompaniesInfo(
                [...allCompaniesInfo].map((_company) =>
                  _company.id === companyDetails?.id
                    ? {
                      ...companyDetails,
                      additional_images: [...res.data?.additional_images],
                    }
                    : _company,
                ),
              ),
            );
          } else {
            dispatch(
              setExpertInfo({
                ...expertDetails,
                additional_images: [...res.data?.additional_images],
              }),
            );
            UserService.setExpertInfo({
              ...expertDetails,
              additional_images: [...res.data?.additional_images],
            });
          }
          setImages(null);
          setImageSource([]);
        },
        onError(res) {
          addToast(
            res?.response?.data || { message: 'Failed to upload images' },
            'warning',
          );
        },
      },
    );
  };

  const removeExtraImages = (index) => {
    if (isLoadingImageUpload) return;
    setImageIndex(index);
    const imageList =
      accountType === 'founder'
        ? [...companyDetails.additional_images]
        : [...expertDetails?.additional_images];
    imageList.splice(index, 1);

    mutateImageUpload(
      {
        key:
          accountType === 'founder'
            ? `companies/manageExtraImages/${companyDetails?.id}`
            : `experts/manageExtraImages`,
        method: 'post',
        data: { files: imageList },
      },
      {
        onSuccess(res) {
          addToast(res);
          if (accountType === 'founder') {
            dispatch(
              setCompanyInfo({
                ...companyDetails,
                additional_images: [...res.data?.additional_images],
              }),
            );
            UserService.setCompanyInfo({
              ...companyDetails,
              additional_images: [...res.data?.additional_images],
            });
            UserService.setAllCompaniesInfo(
              [...allCompaniesInfo].map((_company) =>
                _company.id === companyDetails?.id
                  ? {
                    ...companyDetails,
                    additional_images: [...res.data?.additional_images],
                  }
                  : _company,
              ),
            );
            dispatch(
              setAllCompaniesInfo(
                [...allCompaniesInfo].map((_company) =>
                  _company.id === companyDetails?.id
                    ? {
                      ...companyDetails,
                      additional_images: [...res.data?.additional_images],
                    }
                    : _company,
                ),
              ),
            );
          } else {
            dispatch(
              setExpertInfo({
                ...expertDetails,
                additional_images: [...res.data?.additional_images],
              }),
            );
            UserService.setExpertInfo({
              ...expertDetails,
              additional_images: [...res.data?.additional_images],
            });
          }
          setImageIndex(null);
        },
        onError(res) {
          addToast(
            res?.response?.data || { message: 'Failed to remove image' },
            'error',
          );
          setImageIndex(null);
        },
      },
    );
  };

  const editSurvey = () => {
    onOpenEditSurvey();
  };

  const editExpertDetails = () => {
    onOpenEditExpert();
  };

  const closeEditSurvey = () => {
    UserService.setCompleteSurveyAction(false);
    dispatch(setCompleteSurvey(false));
    onCloseEditSurvey();
  };

  const closeEditExpert = () => {
    onCloseEditExpert();
  };

  const onChangeCurrentCompanyIndex = (e) => {
    setCurrentCompanyIndex(e.target.value);
    dispatch(setCompanyInfo(allCompaniesInfo[Number(e.target.value)]));
    UserService.setCompanyInfo(allCompaniesInfo[Number(e.target.value)]);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return url.startsWith('https://');
    } catch {
      return false;
    }
  };

  const submitCompanydata = () => {
    if (websiteUrlError) {
      return;
    }

    const formData = new FormData();
    formData.append('title', filledData?.title);
    formData.append('description', filledData?.description);
    formData.append('pitch_deck', filledData?.pitch_deck);
    formData.append('fundCategoryId', filledData?.fundCategoryId || '');
    formData.append('problem', filledData?.problem);
    formData.append('solution', filledData?.solution);
    formData.append('market_growth', filledData?.market_growth);
    formData.append('revenue_model', filledData?.revenue_model);
    formData.append('competition', filledData?.competition);
    formData.append('fund_use', filledData?.fund_use);
    formData.append('stakeholders', filledData?.stakeholders);
    formData.append('customer_type', filledData?.customer_type);
    if (filledData?.incorporationLocationId?.length > 0) {
      filledData.incorporationLocationId.forEach((data) => {
        formData.append('incorporationLocationId[]', data);
      });
    } else {
      formData.append('incorporationLocationId', '');
    }
    if (accountType === 'founder') {
      if (
        typeof filledData?.companyStageId === 'number' ||
        filledData?.companyStageId?.length > 0
      ) {
        formData.append('companyStageId[]', filledData.companyStageId);
      } else {
        formData.append('companyStageId', '');
      }
      if (
        typeof filledData?.businessModelId === 'number' ||
        filledData?.businessModelId?.length > 0
      ) {
        formData.append('businessModelId[]', filledData.businessModelId);
      } else {
        formData.append('businessModelId', '');
      }
      if (
        typeof filledData.businessCategoryId === 'number' ||
        filledData?.businessCategoryId?.length > 0
      ) {
        formData.append('businessCategoryId[]', filledData.businessCategoryId);
      } else {
        formData.append('businessCategoryId', '');
      }
      formData.append('valuation', filledData?.valuation || 0);

      formData.append(
        'number_of_investors',
        filledData?.number_of_investors || 0,
      );

      formData.append('website_url', filledData?.website_url);
      formData.append('funding_target', filledData?.funding_target || 0);
      formData.append('video_url', filledData?.video_url);
      formData.append('social_media_url', filledData?.social_media_url);
    }
    if (accountType === 'investor') {
      formData.append('job_title', filledData?.job_title);
      if (filledData?.companyStageId?.length > 0) {
        filledData.companyStageId.forEach((data) => {
          formData.append('companyStageId[]', data);
        });
      } else {
        formData.append('companyStageId', '');
      }
      if (filledData?.businessModelId?.length > 0) {
        filledData.businessModelId.forEach((data) => {
          formData.append('businessModelId[]', data);
        });
      } else {
        formData.append('businessModelId', '');
      }
      if (filledData?.businessCategoryId?.length > 0) {
        filledData.businessCategoryId.forEach((data) => {
          formData.append('businessCategoryId[]', data);
        });
      } else {
        formData.append('businessCategoryId', '');
      }
      if (filledData?.investmentlocationId?.length > 0) {
        filledData.investmentlocationId.forEach((data) => {
          formData.append('investmentlocationId[]', data);
        });
      } else {
        formData.append('investmentlocationId', '');
      }
    }
    mutate(
      { key: `companies/${companyDetails?.id}`, method: 'put', data: formData },
      {
        onSuccess(res) {
          addToast(res);
          const updatedData = res?.data?.updatedData;
          UserService.setAllCompaniesInfo(
            [...allCompaniesInfo].map((_company) =>
              _company.id === companyDetails?.id
                ? { ...companyDetails, ...updatedData }
                : _company,
            ),
          );
          dispatch(
            setAllCompaniesInfo(
              [...allCompaniesInfo].map((_company) =>
                _company.id === companyDetails?.id
                  ? { ...companyDetails, ...updatedData }
                  : _company,
              ),
            ),
          );
          dispatch(setCompanyInfo({ ...companyDetails, ...updatedData }));
          UserService.setCompanyInfo({ ...companyDetails, ...updatedData });
          onToggleEditSurvey();
          topRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
          if (accountType === 'founder') {
            setSearchParams('');
            setTimeout(() => {
              evaluateCompanyProfile(
                { ...companyDetails, ...updatedData },
                navigate,
              );
            }, 1000);
          }
          closeEditSurvey();
        },
        onError(res) {
          addToast(
            res?.response?.data || { message: 'Failed to update company data' },
            'warning',
          );
        },
      },
    );
  };

  const submitExpertData = () => {
    const _data = {
      ...expertFilledData,
      category_id:
        selected?.id ||
        list.filter(({ title }) => title == 'Other')[0]?.id ||
        '',
    };

    if (selectedLocation?.id) _data['country_id'] = selectedLocation?.id;
    mutate(
      { key: `experts/${userDetails?.id}`, method: 'post', data: _data },
      {
        onSuccess(res) {
          addToast(res);
          const _expert = res?.data?.expert || {};
          const _user = res?.data?.user || {};
          dispatch(setUserInfo({ ...userDetails, ..._user }));
          dispatch(setExpertInfo({ ...expertDetails, ..._expert }));
          UserService.setUserInfo({ ...userDetails, ..._user });
          UserService.setExpertInfo({ ...expertDetails, ..._expert });
          setSearchValue(_expert?.expert_category?.title || '');
          setSearchLocationValue(_expert?.location?.title || '');
          onToggleEditExpert();
        },
        onError(res) {
          addToast(
            {
              message:
                res?.response?.data?.message ||
                res?.message ||
                'Failed to update',
            },
            'error',
          );
        },
      },
    );
  };

  const handlePasswordReset = (e) => {
    e.preventDefault();
    mutate(
      {
        key: `users/${userDetails?.id}/update-password`,
        method: 'put',
        data: password,
      },
      {
        onSuccess(res) {
          addToast(res);
          setShowPasswordReset(false);
        },
      },
    );
  };

  const handlePasswordChange = (e) => {
    setPassword((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  useEffect(() => {
    let edit = location?.search?.slice(6) || null;
    if (edit && location?.search?.includes('?edit=')) {
      accountType === 'expert' ? onOpenEditExpert() : onOpenEditSurvey();
      setTimeout(() => {
        if (refs[`${edit}Ref`]?.current) {
          refs[`${edit}Ref`]?.current?.scrollIntoView({
            block: 'nearest',
            inline: 'start',
          });
          addToast(
            toastAlerts[`${edit}`],
            edit === 'pitchDeck' || edit === 'videoUrl' ? 'error' : 'info',
            edit === 'pitchDeck' ? 'top' : 'top-right',
          );
        }
      }, 2000);
    }

    if (edit && location?.search?.includes('?tab=')) {
      edit = location?.search?.slice(5);
      if (edit == 'slots') setTabIndex(0);
    }
  }, [location.search, surveyData?.fundCategories]);

  const handleNotification = (e, type) => {
    e.preventDefault();

    const value = e.target.checked;
    if (type === 'email_notification') {
      setEmailNotf(value);
    } else if (type === 'push_notification') {
      setPushNotf(value);
    } else if (type === 'promotional_newsletter') {
      setPromoNotf(value);
    }

    const data = {
      type,
      value: value,
    };
    mutate(
      {
        key: `users/user-update-notf-settings`,
        method: 'post',
        data,
      },
      {
        onSuccess(res) {
          addToast(res);
          setShowPasswordReset(false);
        },
      },
    );
  };

  const handleSystemPreferences = (e, type) => {
    e.preventDefault();

    const value = e.target.checked;
    if (type === 'inboxNotification') {
      setInboxNotification(value);
    } else if (type === 'friendRequestNotification') {
      setFriendRequestNotification(value);
    }

    const data = {
      [type]: value,
    };
    mutate(
      {
        key: `users/system-preferences`,
        method: 'put',
        data,
      },
      {
        onSuccess(res) {
          addToast('Preferences updated');
        },
      },
    );
  };


  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: `${userDetailsData?.first_name} ${userDetailsData?.last_name}`,
      email: userDetailsData?.email,
      phone_number: userDetailsData?.phone_number,
      imageUrl: userDetailsData?.profile_image,
    },
    resolver: yupResolver(accountSettingsSchema),
  });

  useEffect(() => {
    if (userDetailsData) {
      reset({
        name: `${userDetailsData.first_name} ${userDetailsData.last_name}`,
        email: userDetailsData.email,
        phone_number: userDetailsData.phone_number,
        imageUrl: userDetailsData.profile_image,
      });
    }
  }, [userDetailsData, reset]);

  const handleChangeForFile = (e, type) => {
    e.stopPropagation();
    if (!e?.target?.files.length) return;
    const fileData = e.target.files[0];

    const formData = new FormData();
    formData.append('attachment', fileData);

    if (type == 'profile') {
      // setProfilePicture(fileData);
      uploadProfilePhoto(formData, type);
    } else if (type == 'cover') {
      uploadCoverPhoto(formData, type);
    }
  };

  const uploadProfilePhoto = (data) => {
    mutateProfileImageUpload(
      {
        key: `users/${userDetails?.id}/profile`,
        method: 'put',
        data,
      },
      {
        onSuccess(res) {
          addToast(res);
          refetch();
          dispatch(
            setUserInfo({
              ...userDetails,
              profile_image: res.data.profile_image,
            }),
          );
          UserService.setUserInfo({
            ...userDetails,
            profile_image: res.data.profile_image,
          });
        },

        onError(res) {
          addToast(
            res?.response?.data?.message || 'Error uploading profile image',
            'error',
          );
        },
      },
    );
  };

  const uploadCoverPhoto = (data, type) => {
    mutateUpload(
      {
        key: `users/${userDetails?.id}/${type}-image`,
        method: 'put',
        data,
      },
      {
        onSuccess(res) {
          addToast(res);
          refetch();
        },
        onError(res) {
          addToast(
            res?.response?.data?.message || 'Error uploading cover image',
            'error',
          );
        },
      },
    );
  };

  const formSubmit = (data) => {
    mutate(
      {
        key: `users/${loggedInUser.id}/profile`,
        method: 'put',
        data,
      },
      {
        onSuccess(res) {
          addToast('Profile updated successfully');
          queryClient.invalidateQueries(`users/${userDetails.id}`);
        },
        onError(res) {
          addToast(res?.response?.data?.message || 'Error occured', 'error');
        },
      },
    );
  };

  useEffect(() => {
    switch (typeof profilePicture) {
      case 'string':
        // setProfilePicture(profilePicture);
        break;
      case 'object':
        setProfilePicture(
          window.URL.createObjectURL(
            new Blob([profilePicture], { type: 'image' }),
          ),
        );
        break;
      default:
        setProfilePicture('');
    }
  }, [profilePicture]);

  const handleChange = (e) => {
    setFilledData((prev) => {
      const _obj = {
        ...prev,
        [e.target.name]: e.target.value,
      };
      localStorage.setItem('companyData', JSON.stringify(_obj));
      return _obj;
    });
  };

  const handleAddCompanyDataChange = (e) => {
    setAddCompanyData((prev) => {
      const _obj = {
        ...prev,
        [e.target.name]: e.target.value,
      };
      localStorage.setItem('companyData', JSON.stringify(_obj));
      return _obj;
    });
  };

  const handleExpertChange = (e) => {
    setExpertFilledData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFileInput = (e) => {
    setFilledData((prev) => ({
      ...prev,
      [e.target.name]: e.target.files[0],
    }));
  };

  const handleAddCompanyFileInput = (e) => {
    setAddCompanyData((prev) => ({
      ...prev,
      [e.target.name]: e.target.files[0],
    }));
  };

  useEffect(() => {
    if (shouldGoToEditSurvey === true) {
      onOpenEditSurvey();
    }
  }, [shouldGoToEditSurvey, onOpenEditSurvey]);


  useEffect(() => {
    let prevAddCompanyData = localStorage.getItem('companyData');
    if (prevAddCompanyData && prevAddCompanyData != 'undefined') {
      setAddCompanyData(JSON.parse(prevAddCompanyData));
    }
  }, []);

  const deletePhoto = () => {
    let data = {};
    mutate(
      { key: `users/user-delete-photo`, method: 'put', data },
      {
        onSuccess(res) {
          addToast(res);
          refetch();
        },
      },
    );
  };

  const formSubmitResetPassword = () => {
    let data = {
      email: userDetails?.email,
    };

    setLoading(true);
    mutate(
      { key: 'forgot-password', method: 'post', data },
      {
        onSuccess(res) {
          setLoading(false);

          // showCheckEmail();
          // onToggleCheckEmail();
          addToast({ message: res.message, type: 'info' });
        },
      },
    );
  };

  const addExtraCompany = () => {
    try {
      if (!addCompanyData.title.trim()) {
        addToast({ message: 'Please fill in company title', type: 'info' });
        return;
      }

      const formData = new FormData();
      formData.append('title', addCompanyData?.title);
      formData.append('description', addCompanyData?.description);
      formData.append('website_url', addCompanyData?.website_url);
      formData.append('pitch_deck', addCompanyData?.pitch_deck);
      formData.append('fundCategoryId', addCompanyData?.fundCategoryId || '');
      formData.append('problem', addCompanyData?.problem);
      formData.append('solution', addCompanyData?.solution);
      formData.append('market_growth', addCompanyData?.market_growth);
      formData.append('revenue_model', addCompanyData?.revenue_model);
      formData.append('competition', addCompanyData?.competition);
      formData.append('fund_use', addCompanyData?.fund_use);
      formData.append('stakeholders', addCompanyData?.stakeholders);
      formData.append('customer_type', addCompanyData?.customer_type);
      if (addCompanyData?.incorporationLocationId?.length > 0) {
        addCompanyData?.incorporationLocationId.forEach((data) => {
          formData.append('incorporationLocationId[]', data);
        });
      } else {
        formData.append('incorporationLocationId', '');
      }
      if (accountType === 'founder') {
        if (
          typeof addCompanyData?.companyStageId === 'number' ||
          addCompanyData?.companyStageId?.length > 0
        ) {
          formData.append('companyStageId[]', addCompanyData?.companyStageId);
        } else {
          formData.append('companyStageId', '');
        }
        if (
          typeof addCompanyData?.businessModelId === 'number' ||
          addCompanyData?.businessModelId?.length > 0
        ) {
          formData.append('businessModelId[]', addCompanyData?.businessModelId);
        } else {
          formData.append('businessModelId', '');
        }
        if (
          typeof addCompanyData?.businessCategoryId === 'number' ||
          addCompanyData?.businessCategoryId?.length > 0
        ) {
          formData.append(
            'businessCategoryId[]',
            addCompanyData?.businessCategoryId,
          );
        } else {
          formData.append('businessCategoryId', '');
        }

        formData.append('website_url', addCompanyData?.website_url);
        formData.append('social_media_url', addCompanyData?.social_media_url);
      }
      if (accountType === 'investor') {
        formData.append('job_title', addCompanyData?.job_title);
        if (addCompanyData?.companyStageId?.length > 0) {
          addCompanyData?.companyStageId.forEach((data) => {
            formData.append('companyStageId[]', data);
          });
        } else {
          formData.append('companyStageId', '');
        }
        if (addCompanyData?.businessModelId?.length > 0) {
          addCompanyData?.businessModelId.forEach((data) => {
            formData.append('businessModelId[]', data);
          });
        } else {
          formData.append('businessModelId', '');
        }
        if (addCompanyData?.businessCategoryId?.length > 0) {
          addCompanyData?.businessCategoryId.forEach((data) => {
            formData.append('businessCategoryId[]', data);
          });
        } else {
          formData.append('businessCategoryId', '');
        }
        if (addCompanyData?.investmentlocationId?.length > 0) {
          addCompanyData?.investmentlocationId.forEach((data) => {
            formData.append('investmentlocationId[]', data);
          });
        } else {
          formData.append('investmentlocationId', '');
        }
      }
      mutateAddExtraCompany(
        { key: `companies/addExtraCompany`, method: 'post', data: formData },
        {
          onSuccess(res) {
            addToast({ message: res.message, type: 'info' });
            onCloseAddCompanyModal();
            setAddCompanyData({
              title: '',
              job_title: '',
              description: '',
              pitch_deck: '',
              video_url: '',
              businessCategoryId: null,
              businessModelId: null,
              fundCategoryId: null,
              incorporationLocationId: null,
              investmentlocationId: null,
              companyStageId: null,
              problem: null,
              solution: null,
              market_growth: null,
              revenue_model: null,
              competition: null,
              fund_use: null,
              stakeholders: null,
              customer_type: null,
            });
            localStorage.removeItem('companyData');

            const companyData = res?.data?.company;
            if (!companyData) return;
            UserService.setAllCompaniesInfo([
              ...allCompaniesInfo,
              { ...companyData },
            ]);
            dispatch(
              setAllCompaniesInfo([...allCompaniesInfo, { ...companyData }]),
            );
            dispatch(setCompanyInfo({ ...companyData }));
            UserService.setCompanyInfo({ ...companyData });
          },
        },
      );
    } catch (e) {
      //console.log(e)
    }
  };

  const onAddVideoLink = (value) => {
    setVideoLink(value);
    let remainingSlots =
      8 -
      (accountType === 'founder'
        ? companyDetails?.additional_images?.length
        : expertDetails?.additional_images?.length) || 0;
    if (remainingSlots <= 0) {
      setTimeout(() => {
        addToast({
          message: `Cannot add anymore portfolio links or images`,
          type: 'warning',
        });
      }, 500);
      return;
    }
    if (!value) {
      setTimeout(() => {
        addToast({
          message: `Please add a link`,
          type: 'warning',
        });
      }, 500);
      return;
    }
    const attachments = [value];
    let formData = new FormData();
    for (let i of attachments) {
      formData.append('links', i);
    }
    mutateImageUpload(
      {
        key:
          accountType === 'founder'
            ? `companies/addExtraImages/${companyDetails?.id}`
            : `experts/addExtraImages`,
        method: 'patch',
        data: formData,
      },
      {
        onSuccess(res) {
          addToast(res);
          if (accountType === 'founder') {
            dispatch(
              setCompanyInfo({
                ...companyDetails,
                additional_images: [...res.data?.additional_images],
              }),
            );
            UserService.setCompanyInfo({
              ...companyDetails,
              additional_images: [...res.data?.additional_images],
            });
            UserService.setAllCompaniesInfo(
              [...allCompaniesInfo].map((_company) =>
                _company.id === companyDetails?.id
                  ? {
                    ...companyDetails,
                    additional_images: [...res.data?.additional_images],
                  }
                  : _company,
              ),
            );
            dispatch(
              setAllCompaniesInfo(
                [...allCompaniesInfo].map((_company) =>
                  _company.id === companyDetails?.id
                    ? {
                      ...companyDetails,
                      additional_images: [...res.data?.additional_images],
                    }
                    : _company,
                ),
              ),
            );
          } else {
            // console.log(res.data?.additional_images);
            dispatch(
              setExpertInfo({
                ...expertDetails,
                additional_images: [...res.data?.additional_images],
              }),
            );
            UserService.setExpertInfo({
              ...expertDetails,
              additional_images: [...res.data?.additional_images],
            });
          }
          setImages(null);
          setVideoLink('');
          setImageSource([]);
        },
        onError(res) {
          addToast(
            res?.response?.data || { message: 'Failed to upload images' },
            'warning',
          );
        },
      },
    );
  };

  // Add this state near the other state declarations at the top of the component
  const [websiteUrlError, setWebsiteUrlError] = useState('');

  const isCompanyFormIncomplete = () => {
    // List all required fields for the company form
    let requiredFields = [
      'title',
      'description',
      'companyStageId',
      'fundCategoryId',
      'incorporationLocationId',
      'businessModelId',
      'businessCategoryId'
    ];

    if (accountType === "founder") {
      requiredFields = [...requiredFields,
        'number_of_investors',
        'pitch_deck',
        'video_url',
        'problem',
        'solution',
        'market_growth',
        'revenue_model',
        'competition',
        'fund_use',
        'stakeholders',
        'customer_type',
        'website_url',
        'social_media_url',
        'valuation',
        'funding_target'
      ];
    } else if (accountType === "investor") {
      requiredFields = [...requiredFields, "job_title"];
    }

    for (let field of requiredFields) {
      const value = filledData[field];
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return true;
      }
    }
    // Also check for website URL error
    if (websiteUrlError) return true;
    return false;
  };

  const SUBSCRIPTION_FLAG = process.env.REACT_APP_SUBSCRIPTION_FLAG;

  const currrentPlanName = loggedInUserSubscription?.subscription_plan?.name;

  const isFreePlan = currrentPlanName?.trim().toLowerCase().includes("free");

  return (
    <Box>
      {isLoadingUserDetails && !isUserDetailsError && <Loader />}
      <DeleteAccountModal
        isOpen={deleteAccountModalIsOpen}
        onClose={closeDeleteAccountModal}
      />
      <EmbedVideoLinkModal
        isOpen={createEmbedVideoIsOpen}
        onClose={onCloseCreateEmbedVideo}
        videoLink={videoLink}
        onAddLink={onAddVideoLink}
      />
      <CustomerSupportModal
        isOpen={submitComplaintModalIsOpen}
        onClose={onCloseSubmitComplaintModal}
      />
      <InviteUserModal
        isOpen={shareInviteModalIsOpen}
        onClose={onCloseShareInviteModal}
      />
      <Header title="Account Settings" />
      <Flex
        className={`flex-col ${isDesktop ? 'max-h-[95vh] w-full fixed pb-10 overflow-y-scroll' : ''
          }`}
      >
        <div ref={topRef}></div>
        <Stack
          className={`${isDesktop ? 'w-[80vw]' : ''}`}
          maxW={'1200px'}
          direction={!isDesktop ? 'column' : 'row'}
          spacing="24px"
          p={8}
        >
          <Box
            w={isDesktop ? '50%' : '100%'}
            boxShadow="0px 0px 8px 4px rgba(19, 74, 112, 0.1)"
            className="rounded-t-lg"
          >
            <VStack spacing={5} align="left">
              <div
                style={{
                  marginBottom: '80px',
                  position: 'relative',
                  width: '100%',
                }}
                className={`h-[10rem] ${data?.data?.user?.cover_image ? '' : 'bg-[#466fc2]'} bg-center bg-no-repeat bg-cover`}
              >
                {data?.data?.user?.cover_image ? (
                  <>
                    <Image
                      src={data?.data?.user?.cover_image}
                      className="max-h-[10rem] rounded-t-lg w-full object-cover"
                      style={{ objectFit: 'cover', height: '100%' }}
                    />
                    {/* Subtle overlay for changing the image */}
                    <Tooltip label="Change banner image" placement="left">
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          changeBannerRef.current?.click();
                        }}
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white p-2 rounded-full cursor-pointer shadow"
                      >
                        {isLoadingUpload ? (
                          <Spinner size="sm" color="brand.primary" />
                        ) : (
                          <Icon
                            as={FiEdit2}
                            color="brand.primary"
                            boxSize={5}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  </>
                ) : (
                  <VStack
                    spacing={1}
                    className="absolute top-1/2 transform -translate-y-1/2 w-full"
                  >
                    {isLoadingUpload ? (
                      <Spinner size="md" color="white" />
                    ) : (
                      <>
                        <Text
                          onClick={(e) => {
                            e.stopPropagation();
                            changeBannerRef.current?.click();
                          }}
                          className="text-white px-3 py-2 cursor-pointer text-center w-full pr-5 font-medium"
                        >
                          Select to change banner image
                        </Text>
                        <Text className="text-white/80 text-sm">
                          Recommended size: 1200 x 300px
                        </Text>
                      </>
                    )}
                  </VStack>
                )}
                <Input
                  accept="image/*"
                  id="image-input-2"
                  style={{ display: 'none' }}
                  type="file"
                  onChange={(e) => handleChangeForFile(e, 'cover')}
                  name="avatar"
                  key="avatar"
                  ref={changeBannerRef}
                />
                <Box
                  className="px-4 flex w-full flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-4"
                  position={'absolute'}
                  bottom={'-70px'}
                >
                  <HStack className="w-full sm:w-auto justify-start">
                    <div style={{ position: 'relative' }}>
                      <Avatar
                        className="border-2 border-white"
                        size={{ base: 'lg', sm: 'xl' }}
                        name={`${userDetails?.firstName} ${userDetails?.lastName}`}
                        src={data?.data?.user?.profile_image}
                        mr={2}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          background: '#ffffff',
                          cursor: 'pointer',
                          borderRadius: '100px',
                        }}
                        className="p-1.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          changeDPRef.current?.click();
                        }}
                      >
                        {isLoadingProfileImageUpload ? (
                          <Spinner size="sm" color="brand.primary" />
                        ) : (
                          <Image
                            h="16px"
                            w="16px"
                            src={EditIcon}
                            alt="dashboard-icon"
                          />
                        )}

                        <Input
                          accept="image/*"
                          id="image-input-2"
                          style={{ display: 'none' }}
                          type="file"
                          onChange={(e) => handleChangeForFile(e, 'profile')}
                          name="avatar"
                          key="avatar"
                          ref={changeDPRef}
                        />
                      </div>
                    </div>
                    {accountType === 'founder' && (
                      <Flex className="flex-row gap-1 items-center">
                        <StarIcon
                          h={3.5}
                          w={3.5}
                          color={
                            userDetails?.rating > 0 ? 'yellow.400' : 'initial'
                          }
                        />
                        <StarIcon
                          h={3.5}
                          w={3.5}
                          color={
                            userDetails?.rating > 1 ? 'yellow.400' : 'initial'
                          }
                        />
                        <StarIcon
                          h={3.5}
                          w={3.5}
                          color={
                            userDetails?.rating > 2 ? 'yellow.400' : 'initial'
                          }
                        />
                      </Flex>
                    )}
                  </HStack>
                  {(accountType === 'founder') && (
                    <Box className="w-full sm:w-auto flex justify-start sm:justify-end">
                      <Button
                        _hover={{ background: 'brand.primary' }}
                        className="text-white text-sm w-full sm:w-auto"
                        bgColor={'brand.primary'}
                        onClick={() => {
                          if (SUBSCRIPTION_FLAG == "on" && isFreePlan) {
                            navigate(`${window.location.pathname}?upgrade=true`, { replace: true });
                            return;
                          }

                          onOpenAddCompanyModal();
                        }}
                      >
                        <AddIcon />
                        &nbsp;&nbsp;Add Company
                      </Button>
                    </Box>
                  )}
                </Box>
              </div>

              <form
                className="px-7 py-4"
                noValidate
                onSubmit={handleSubmit(formSubmit)}
              >
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  className="mb-9"
                >
                  <Box w="100%" className="flex flex-col gap-6">
                    <Box className="flex flex-col gap-3 w-full">
                      <Text className="text-[#484848] font-medium text-base">
                        Your name
                      </Text>
                      <Input
                        {...register('name')}
                        placeholder="Enter your name"
                        w={'full'}
                        borderColor="#D5DBE2"
                        borderRadius={'16px'}
                        px={'1rem'}
                        py={'24px'}
                        className="p-2"
                        _placeholder={{ color: 'brand.primary' }}
                      />
                      <Text color="#9FA7AD" fontWeight={400} fontSize={12}>
                        You can't change your name now if you changed it within
                        the last 14 days.
                      </Text>
                    </Box>
                    <Box className="flex flex-col gap-3 w-full">
                      <Text className="text-[#484848] font-medium text-base">
                        Email Address
                      </Text>
                      <Input
                        {...register('email')}
                        disabled
                        placeholder="yourname@example.com"
                        w={'full'}
                        borderColor="#D5DBE2"
                        borderRadius={'16px'}
                        px={'1rem'}
                        py={'24px'}
                        className="p-2"
                        _placeholder={{ color: 'brand.primary' }}
                      />
                      <Text color="#9FA7AD" fontWeight={400} fontSize={12}>
                        Enter an email you check regularly. Any alerts you
                        activate will be sent there.
                      </Text>
                    </Box>

                    <Box className="flex flex-col gap-3 w-full">
                      <Text className="text-[#484848] font-medium text-base">
                        Phone Number
                      </Text>
                      <Input
                        {...register('phone_number')}
                        placeholder="+2348060000000"
                        w={'full'}
                        borderColor="#D5DBE2"
                        borderRadius={'16px'}
                        px={'1rem'}
                        py={'24px'}
                        className="p-2"
                        _placeholder={{ color: 'brand.primary' }}
                        onKeyDown={(e) => {
                          // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
                          if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
                            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                            (e.keyCode === 65 && e.ctrlKey === true) ||
                            (e.keyCode === 67 && e.ctrlKey === true) ||
                            (e.keyCode === 86 && e.ctrlKey === true) ||
                            (e.keyCode === 88 && e.ctrlKey === true)) {
                            return;
                          }
                          // Allow: + at the beginning
                          if (e.key === '+' && e.target.selectionStart === 0) {
                            return;
                          }
                          // Ensure that it is a number and stop the keypress
                          if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <Text color="#9FA7AD" fontWeight={400} fontSize={12}>
                        Enter your phone number.
                      </Text>

                      {errors?.phone_number?.message && (
                        <span className="text-sm text-red-400">
                          {errors?.phone_number?.message}
                        </span>
                      )}
                    </Box>

                    <Box className="flex flex-col gap-3 w-full">
                      <Text color="#000" fontWeight={400}>
                        Current password
                      </Text>
                      <Box w={'full'}>
                        <InputGroup>
                          <Input
                            {...register('password')}
                            placeholder="Your current password"
                            w="full"
                            borderColor="#D5DBE2"
                            borderRadius={'16px'}
                            px={'1rem'}
                            py={'24px'}
                            type={showPassword ? 'text' : 'password'}
                            _placeholder={{ color: '#9FA7AD' }}
                          />
                          <InputRightElement
                            py={'24px'}
                            // pointerEvents="none"
                            className="cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowPassword(!showPassword);
                            }}
                            children={
                              <Icon
                                as={AiOutlineEyeInvisible}
                                color="#9FA7AD"
                              />
                            }
                          />
                        </InputGroup>

                        {errors?.password?.message && (
                          <span className="text-sm text-red-400">
                            {errors?.password?.message}
                          </span>
                        )}
                      </Box>
                      <Text color="#9FA7AD" fontWeight={400} fontSize={12}>
                        To make any changes, you must enter your current
                        password.
                      </Text>
                    </Box>
                  </Box>
                </Flex>
                <Box className="mb-9">
                  <Text
                    color="brand.primary"
                    className="mb-4"
                    fontSize={'1.2rem'}
                    fontWeight={500}
                  >
                    Notifications
                  </Text>
                  <Flex className="flex-col gap-6 w-full">
                    <Flex className="flex-col gap-3 w-full">
                      <Text color="#979797" fontWeight={500} fontSize={'1rem'}>
                        How would you like to recieve your notifications on Seed
                        A Founder
                      </Text>
                      <Wrap className="flex flex-col">
                        <WrapItem>
                          <Checkbox
                            value={pushNotf}
                            isChecked={pushNotf}
                            fontSize={14}
                            color="brand.primary"
                            borderColor="brand.primary"
                            onChange={(e) =>
                              handleNotification(e, 'push_notification')
                            }
                          >
                            Push notifications
                          </Checkbox>
                        </WrapItem>
                        <WrapItem>
                          <Checkbox
                            value={emailNotf}
                            isChecked={emailNotf}
                            fontSize={14}
                            color="brand.primary"
                            borderColor="brand.primary"
                            onChange={(e) =>
                              handleNotification(e, 'email_notification')
                            }
                          >
                            Email notifications
                          </Checkbox>
                        </WrapItem>
                      </Wrap>
                    </Flex>

                    <Flex className="flex-col gap-3 w-full">
                      <Text color="#979797" fontWeight={500} fontSize={'1rem'}>
                        Never miss any activity on Seed A Founder
                      </Text>
                      <Wrap>
                        <WrapItem>
                          <Checkbox
                            value={inboxNotification}
                            isChecked={inboxNotification}
                            fontSize={14}
                            color="brand.primary"
                            borderColor="brand.primary"
                            onChange={(e) =>
                              handleSystemPreferences(e, 'inboxNotification')
                            }
                          >
                            Inbox Messages
                          </Checkbox>
                        </WrapItem>
                        <WrapItem>
                          <Checkbox
                            value={friendRequestNotification}
                            isChecked={friendRequestNotification}
                            fontSize={14}
                            color="brand.primary"
                            borderColor="brand.primary"
                            onChange={(e) =>
                              handleSystemPreferences(
                                e,
                                'friendRequestNotification',
                              )
                            }
                          >
                            Friend Requests
                          </Checkbox>
                        </WrapItem>
                        <WrapItem>
                          <Checkbox
                            value={promoNotf}
                            isChecked={promoNotf}
                            fontSize={14}
                            color="brand.primary"
                            borderColor="brand.primary"
                            onChange={(e) =>
                              handleNotification(e, 'promotional_newsletter')
                            }
                          >
                            Receive Promotional emails and newsletter
                          </Checkbox>
                        </WrapItem>
                      </Wrap>
                    </Flex>
                  </Flex>
                </Box>
                <Flex className="mt-3 flex-col gap-3 w-full items-center">
                  <Button
                    bgColor="brand.primary"
                    color="#fff"
                    fontSize="14px"
                    borderRadius={4}
                    px={4}
                    type="submit"
                    w={'full'}
                    // onClick={handleSubmit(formSubmit)}
                    _hover={{ background: 'brand.primary' }}
                  >
                    {isLoading ? 'Updating...' : 'Save changes'}
                  </Button>
                  <Box
                    onClick={formSubmitResetPassword}
                    cursor="pointer"
                    w={'full'}
                    minH="30px"
                    borderColor="brand.primary"
                    borderWidth={1}
                    borderRadius="5px"
                  >
                    <Flex justifyContent="space-between" w={'full'} p={2}>
                      <Text
                        className="text-center text-sm w-full"
                        color="brand.primary"
                        fontWeight={500}
                      >
                        Reset my password
                      </Text>
                      {loading && <Spinner />}

                      {/* {!showPasswowordReset ? <AiOutlineRight /> : <AiOutlineUp />} */}
                    </Flex>
                  </Box>
                </Flex>
              </form>

              <Collapse in={showPasswowordReset}>
                <form onSubmit={handlePasswordReset}>
                  <InputGroup mb={3}>
                    <Flex
                      borderWidth="1px"
                      borderColor="brand.primary"
                      borderRadius="5px"
                      alignItems="center"
                    >
                      <InputLeftAddon
                        borderWidth="0px"
                        bg=""
                        children={<Image src={lock} size={30} />}
                      />
                      <Input
                        focusBorderColor="#"
                        borderWidth="0px"
                        variant="outline"
                        name="currentPassword"
                        onChange={(e) => handlePasswordChange(e)}
                        placeholder="Your current password"
                        type="password"
                        w="250px"
                        h="50px"
                      />
                    </Flex>
                  </InputGroup>
                  <InputGroup mb={3}>
                    <Flex
                      borderWidth="1px"
                      borderColor="brand.primary"
                      borderRadius="5px"
                      alignItems="center"
                    >
                      <InputLeftAddon
                        borderWidth="0px"
                        bg=""
                        children={<Image src={lock} size={30} />}
                      />
                      <Input
                        focusBorderColor="#"
                        borderWidth="0px"
                        variant="outline"
                        name="newPassword"
                        onChange={(e) => handlePasswordChange(e)}
                        placeholder="New password, at Least 6 characters"
                        type="password"
                        w="250px"
                        h="50px"
                      />
                    </Flex>
                  </InputGroup>
                  <InputGroup mb={3}>
                    <Flex
                      borderWidth="1px"
                      borderColor="brand.primary"
                      borderRadius="5px"
                      alignItems="center"
                    >
                      <InputLeftAddon
                        borderWidth="0px"
                        bg=""
                        children={<Image src={lock} size={30} />}
                      />
                      <Input
                        focusBorderColor="#"
                        borderWidth="0px"
                        variant="outline"
                        name="confirmNewPassword"
                        onChange={(e) => handlePasswordChange(e)}
                        placeholder="Confirm New password"
                        type="password"
                        w="250px"
                        h="50px"
                      />
                    </Flex>
                  </InputGroup>
                  <BrandButton
                    colorScheme="blue"
                    w="180px"
                    text="Reset Password"
                    isLoading={isLoading}
                    type="submit"
                  />
                </form>
              </Collapse>
            </VStack>
            {/* <Box  className="px-7 py-4">
              <Text color="brand.primary" fontSize={"1.2rem"} fontWeight={500}>
                Contact Support
              </Text>
              <Text color="#000" fontWeight={400} fontSize={14}>
                Click to submit your complaint, report a bug, or contact support.
              </Text>
              <Button
                mt={4}
                color="#fff"
                variant={"brand.primary"}
                _hover={{ background: "brand.primary" }} 
                className="text-white text-sm" 
                bgColor={"brand.primary"}
                borderRadius={4}
                fontSize={14}
                onClick={() => {
                  onOpenSubmitComplaintModal();
                }}
              >
                Contact Support
              </Button>
            </Box> */}
            <Box className="px-7 mt-9 mb-9 flex flex-col gap-3">
              <Text color="brand.primary" fontSize={'1.2rem'} fontWeight={500}>
                Invite A Friend
              </Text>
              <Text color="#000" fontWeight={400} fontSize={14}>
                Enjoying Seed A Founder? Invite a friend!
              </Text>
              <Button
                color="brand.primary"
                bgColor="#fff"
                borderRadius={4}
                fontSize={14}
                border="1px solid #134A70"
                _hover={{ background: 'inherit' }}
                onClick={() => {
                  onOpenShareInviteModal();
                }}
              >
                Invite a Friend
              </Button>
            </Box>
            <Box className="px-7 mt-9 mb-9 flex flex-col gap-3">
              <Text color="brand.primary" fontSize={'1.2rem'} fontWeight={500}>
                Delete Account
              </Text>
              <Text color="#000" fontWeight={400} fontSize={14}>
                This will completely remove your account from Seed A Founder
              </Text>
              <Button
                color="brand.primary"
                bgColor="#fff"
                borderRadius={4}
                fontSize={14}
                border="1px solid #134A70"
                _hover={{ background: 'inherit' }}
                onClick={() => {
                  onOpenDeleteAccountModal();
                }}
              >
                Delete Account
              </Button>
            </Box>
          </Box>

          {/* Right column */}
          {accountType === 'founder' || accountType === 'investor' ? (
            <>
              <Box
                w={!isDesktop ? '100%' : '50%'}
                boxShadow="0px 0px 8px 4px rgba(19, 74, 112, 0.1)"
                borderRadius="10px"
                className="p-2"
              >
                {accountType === 'founder' ? (
                  <Tabs
                    isFitted
                    className="mb-4"
                    index={tabIndex}
                    onChange={handleTabsChange}
                  >
                    <TabList>
                      <Tab>Slots</Tab>
                      <Tab>Profile</Tab>
                    </TabList>
                  </Tabs>
                ) : null}
                {tabIndex === 1 ? (
                  <CompanyProile
                    images={images}
                    isOpenEditSurvey={isOpenEditSurvey}
                    addCompanyModalIsOpen={addCompanyModalIsOpen}
                    viewProfileAsOthers={viewProfileAsOthers}
                    accountType={accountType}
                    tabIndex={tabIndex}
                    editSurvey={editSurvey}
                    currentCompanyIndex={currentCompanyIndex}
                    onChangeCurrentCompanyIndex={onChangeCurrentCompanyIndex}
                    allCompaniesInfo={allCompaniesInfo}
                    isDesktop={isDesktop}
                    companyDetails={companyDetails}
                    closeEditSurvey={closeEditSurvey}
                    isCompanyFormIncomplete={isCompanyFormIncomplete}
                    submitCompanydata={submitCompanydata}
                    isLoading={isLoading}
                    filledData={filledData}
                    setFilledData={setFilledData}
                    surveyData={surveyData}
                    handleChange={handleChange}
                    imageSource={imageSource}
                    handleChangeForImages={handleChangeForImages}
                    imagesInputRef={imagesInputRef}
                    openImageUpload={openImageUpload}
                    uploadExtraImages={uploadExtraImages}
                    isLoadingImageUpload={isLoadingImageUpload}
                    setImages={setImages}
                    setImageSource={setImageSource}
                    removeExtraImages={removeExtraImages}
                    imageIndex={imageIndex}
                    addCompanyData={addCompanyData}
                    setAddCompanyData={setAddCompanyData}
                    handleAddCompanyDataChange={handleAddCompanyDataChange}
                    pitchDeckRef={pitchDeckRef}
                    handleAddCompanyFileInput={handleAddCompanyFileInput}
                    problemRef={problemRef}
                    solutionRef={solutionRef}
                    marketGrowthRef={marketGrowthRef}
                    revenueModelRef={revenueModelRef}
                    competitionRef={competitionRef}
                    fundsUseRef={fundsUseRef}
                    stakeholdersRef={stakeholdersRef}
                    isValidUrl={isValidUrl}
                    setWebsiteUrlError={setWebsiteUrlError}
                    websiteUrlError={websiteUrlError}
                    isLoadingAddExtraCompany={isLoadingAddExtraCompany}
                    addExtraCompany={addExtraCompany}
                    onCloseAddCompanyModal={onCloseAddCompanyModal}
                    companyStageRef={companyStageRef}
                    fundCategoryRef={fundCategoryRef}
                    incorporationLocationRef={incorporationLocationRef}
                    businessModelRef={businessModelRef}
                    businessCategoryRef={businessCategoryRef}
                    handleFileInput={handleFileInput}
                    videoUrlRef={videoUrlRef}
                    customerTypeRef={customerTypeRef}
                  />
                ) : (
                  <MySlots />
                )}
              </Box>
            </>
          ) : (
            <Box
              w={!isDesktop ? '100%' : '50%'}
              boxShadow="0px 0px 8px 4px rgba(19, 74, 112, 0.1)"
              borderRadius="10px"
              p={4}
            >
              <Tabs
                isFitted
                className="mb-4"
                index={tabIndex}
                onChange={handleTabsChange}
              >
                <TabList>
                  <Tab>Slots</Tab>
                  <Tab>Edit Profile</Tab>
                </TabList>
              </Tabs>
              {tabIndex === 0 ? <MySlots /> : null}
              {tabIndex === 1 ? (
                <Flex className="flex-col">
                  {!isOpenEditExpert ? (
                    <Box>
                      <Text color="brand.primary" fontWeight={500}>
                        Expert details
                      </Text>
                      <Box fontSize={{ base: 12, '2xl': 16 }} mt="5%">
                        <Flex className="flex flex-wrap lg:flex-nowrap w-full gap-1 mb-4">
                          <Text fontWeight={500} color="#9FA7AD">
                            Company Name
                          </Text>
                          <Text color="#000" align="right">
                            {expertDetails?.company_name}
                          </Text>
                        </Flex>
                        <Flex className="flex flex-wrap lg:flex-nowrap w-full gap-1 mb-4">
                          <Text fontWeight={500} color="#9FA7AD">
                            Bio:
                          </Text>
                          <Text color="#000" align="right">
                            {userDetails?.bio}
                          </Text>
                        </Flex>
                        <Flex className="flex flex-wrap lg:flex-nowrap w-full gap-1 mb-4">
                          <Text fontWeight={500} color="#9FA7AD">
                            Phone Number:
                          </Text>
                          <Text color="#000" align="right">
                            {userDetails?.phone_number}
                          </Text>
                        </Flex>
                        <Flex className="flex flex-wrap lg:flex-nowrap w-full gap-1 mb-4">
                          <Text fontWeight={500} color="#9FA7AD">
                            Position:
                          </Text>
                          <Text color="#000" align="right">
                            {userDetails?.headline}
                          </Text>
                        </Flex>
                        <Flex className="flex flex-wrap lg:flex-nowrap w-full gap-1 mb-4">
                          <Text fontWeight={500} color="#9FA7AD">
                            Category:
                          </Text>
                          <Text color="#000" align="right">
                            {expertDetails?.expert_category?.title}
                          </Text>
                        </Flex>
                        <Flex className="flex flex-wrap lg:flex-nowrap w-full gap-1 mb-4">
                          <Text fontWeight={500} color="#9FA7AD">
                            Location:
                          </Text>
                          <Text color="#000" align="right">
                            {expertDetails?.location?.title}
                          </Text>
                        </Flex>
                        <Tooltip label="See how your profile appears to other users">
                          <Button
                            leftIcon={<Icon as={FaUserAlt} />}
                            borderRadius={4}
                            color="brand.primary"
                            bgColor="#fff"
                            fontSize="14px"
                            px={4}
                            border="1px solid"
                            borderColor="brand.primary"
                            type="submit"
                            w={'full'}
                            className="mt-4"
                            _hover={{ background: 'rgba(0, 0, 0, 0.05s)' }}
                            onClick={viewProfileAsOthers}
                          >
                            View Profile
                          </Button>
                        </Tooltip>
                        <Button
                          leftIcon={<Icon as={FiEdit2} />}
                          borderRadius={4}
                          color="#fff"
                          bgColor="brand.primary"
                          fontSize="14px"
                          px={4}
                          mt={6}
                          type="submit"
                          w={'full'}
                          className="my-4"
                          _hover={{ background: 'brand.primary' }}
                          onClick={editExpertDetails}
                        >
                          Edit Profile
                        </Button>
                      </Box>
                      <Box className="mt-8">
                        <Text
                          color="brand.primary"
                          className="text-md font-bold"
                        >
                          Portfolio Media
                        </Text>
                        <Text className="my-2">
                          {`Media slots left: ${8 - (expertDetails?.additional_images?.length || 0) - (imageSource?.length || 0)}`}
                        </Text>
                        <>
                          {images?.length > 0 ? (
                            <Box className="flex my-5 flex-col gap-5">
                              <Box className="flex items-center flex-wrap gap-3">
                                {imageSource.map((image, index) => (
                                  <Image
                                    key={index}
                                    src={image}
                                    className={
                                      'h-[165px] w-[165px] object-contain'
                                    }
                                  />
                                ))}
                                <Input
                                  accept="image/*"
                                  id="image-attachments"
                                  type="file"
                                  onChange={(e) => handleChangeForImages(e)}
                                  name="attachments"
                                  key="image-attachments"
                                  hidden
                                  multiple
                                  ref={imagesInputRef}
                                />
                                {images ? null : (
                                  <Box className="flex flex-wrap gap-2 ">
                                    <Box
                                      onClick={openImageUpload}
                                      className="p-3 h-[130px] w-[140px] flex flex-col items-center justify-center rounded border border-dashed cursor-pointer"
                                    >
                                      <FaImage fontSize={'xl'} />
                                      <Text className="text-[10px] text-center">
                                        Click to upload image
                                      </Text>
                                    </Box>
                                    <Box
                                      onClick={onOpenCreateEmbedVideo}
                                      className="p-3 h-[130px] w-[140px] flex flex-col items-center justify-center rounded border border-dashed cursor-pointer"
                                    >
                                      <FaVideo fontSize={'xl'} />
                                      <Text className="text-[10px] text-center">
                                        Click to add video link
                                      </Text>
                                    </Box>
                                  </Box>
                                )}

                                <Button
                                  bgColor="brand.primary"
                                  color="#fff"
                                  borderRadius={4}
                                  px={4}
                                  _hover={{ background: 'brand.primary' }}
                                  onClick={uploadExtraImages}
                                  className="w-full mx-auto"
                                >
                                  {isLoadingImageUpload ? (
                                    <Flex>
                                      <Spinner />
                                    </Flex>
                                  ) : (
                                    `Upload ${imageSource?.length > 1 ? 'images' : 'image'}`
                                  )}
                                </Button>

                                <Button
                                  bgColor="red.500"
                                  color="#fff"
                                  borderRadius={4}
                                  px={4}
                                  _hover={{ background: 'red.500' }}
                                  onClick={() => {
                                    setImages(null);
                                    setImageSource([]);
                                  }}
                                  className="w-full mx-auto"
                                >
                                  Clear selection
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box className="flex flex-wrap items-center justify-center md:justify-start my-5 gap-3">
                              {expertDetails?.additional_images?.length > 0 ? (
                                expertDetails?.additional_images?.map(
                                  (media, index) => {
                                    return (
                                      <Box
                                        key={index}
                                        className="relative max-w-[165px]"
                                      >
                                        <Text
                                          onClick={() =>
                                            removeExtraImages(index)
                                          }
                                          className={`text-xs absolute rounded-full font-medium right-[-5px] top-[-7px] bg-white border border-black text-gray-900 p-2 cursor-pointer`}
                                        >
                                          <FaTimes />
                                        </Text>
                                        {index == imageIndex ? (
                                          <Flex className="absolute w-full justify-center top-[40%]">
                                            <Spinner />
                                          </Flex>
                                        ) : null}
                                        <MediaDisplay media={[media]} />
                                      </Box>
                                    );
                                  },
                                )
                              ) : (
                                <Text className="flex items-center p-3 text-gray-500 font-semibold">
                                  Click the button to add media
                                </Text>
                              )}
                              <Input
                                accept="image/*"
                                id="image-attachments"
                                type="file"
                                onChange={(e) => handleChangeForImages(e)}
                                name="attachments"
                                key="image-attachments"
                                hidden
                                multiple
                                ref={imagesInputRef}
                              />
                              <Box className="flex flex-wrap gap-2 ">
                                <Box
                                  onClick={openImageUpload}
                                  className="p-3 h-[130px] w-[140px] flex flex-col items-center justify-center rounded border border-dashed cursor-pointer"
                                >
                                  <FaImage fontSize={'xl'} />
                                  <Text className="text-[10px] text-center">
                                    Click to upload image
                                  </Text>
                                </Box>
                                <Box
                                  onClick={onOpenCreateEmbedVideo}
                                  className="p-3 h-[130px] w-[140px] flex flex-col items-center justify-center rounded border border-dashed cursor-pointer"
                                >
                                  <FaVideo fontSize={'xl'} />
                                  <Text className="text-[10px] text-center">
                                    Click to add video link
                                  </Text>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Heading fontSize="18px" color="brand.primary" mb={4}>
                        Edit Expert data
                      </Heading>
                      <Flex className="flex-col gap-2 my-3">
                        <Text
                          fontWeight={500}
                          fontSize="16px"
                          color="brand.primary"
                          ref={expertCompanyRef}
                        >
                          Enter your company's name?
                          {!expertFilledData?.company_name && (
                            <Text className="text-red-600 text-lg inline font-bold">
                              *
                            </Text>
                          )}
                        </Text>
                        <Input
                          key="company_name"
                          type="text"
                          placeholder="Type here...."
                          defaultValue={expertDetails?.company_name}
                          name="company_name"
                          borderRadius="10px"
                          borderWidth={1}
                          borderColor={
                            !expertFilledData?.company_name
                              ? 'red.500'
                              : 'brand.primary'
                          }
                          h="50px"
                          onChange={(e) => handleExpertChange(e)}
                        />
                      </Flex>
                      <Flex ref={expertBioRef} className="flex-col gap-2 my-3">
                        <Text
                          fontWeight={500}
                          fontSize="16px"
                          color="brand.primary"
                        >
                          Describe yourself in a few words
                          {!expertFilledData?.bio && (
                            <Text className="text-red-600 text-lg inline font-bold">
                              *
                            </Text>
                          )}
                        </Text>
                        <Textarea
                          key="bio"
                          placeholder="Type here...."
                          defaultValue={userDetails?.bio}
                          type="text"
                          name="bio"
                          borderRadius="10px"
                          borderWidth={1}
                          borderColor={
                            !expertFilledData?.bio ? 'red.500' : 'brand.primary'
                          }
                          minH="150px"
                          onChange={(e) => handleExpertChange(e)}
                        />
                      </Flex>
                      <Flex
                        ref={expertPhoneRef}
                        className="flex-col gap-2 my-3"
                      >
                        <Text
                          fontWeight={500}
                          fontSize="16px"
                          color="brand.primary"
                        >
                          Enter your phone number?
                          {!expertFilledData?.phone_number && (
                            <Text className="text-red-600 text-lg inline font-bold">
                              *
                            </Text>
                          )}
                        </Text>
                        <Input
                          key="phone_number"
                          type="number"
                          placeholder="Type here...."
                          defaultValue={userDetails?.phone_number}
                          name="phone_number"
                          borderRadius="10px"
                          borderWidth={1}
                          borderColor={
                            !expertFilledData?.phone_number
                              ? 'red.500'
                              : 'brand.primary'
                          }
                          h="50px"
                          onChange={(e) => handleExpertChange(e)}
                        />
                      </Flex>
                      <Flex
                        ref={expertHeadlineRef}
                        className="flex-col gap-2 my-3"
                      >
                        <Text
                          fontWeight={500}
                          fontSize="16px"
                          color="brand.primary"
                          ref={expertCategoryRef}
                        >
                          Enter your position?
                          {!expertFilledData?.headline && (
                            <Text className="text-red-600 text-lg inline font-bold">
                              *
                            </Text>
                          )}
                        </Text>
                        <Input
                          key="headline"
                          type="text"
                          placeholder="Type here...."
                          defaultValue={userDetails?.headline}
                          name="headline"
                          borderRadius="10px"
                          borderWidth={1}
                          borderColor={
                            !expertFilledData?.headline
                              ? 'red.500'
                              : 'brand.primary'
                          }
                          h="50px"
                          onChange={(e) => handleExpertChange(e)}
                        />
                      </Flex>
                      <div className={`relative w-full`} ref={ref}>
                        <Text
                          mb={2}
                          fontWeight={500}
                          fontSize="16px"
                          color="brand.primary"
                        >
                          Categories
                          {!selected?.id && (
                            <Text className="text-red-600 text-lg inline font-bold">
                              *
                            </Text>
                          )}
                        </Text>
                        <div
                          ref={ref}
                          className={`bg-white cursor-pointer flex justify-between items-center mb-2 max-w-2xl `}
                        >
                          <Input
                            key="category"
                            id="category"
                            value={searchValue}
                            onChange={onChange}
                            placeholder={'Category'}
                            className="border border-[#134A70] w-full py-3 pr-2 pl-4 rounded-lg outline-none focus:outline-none focus-within:outline-none focus-visible:outline-none"
                            borderRadius="10px"
                            borderWidth={1}
                            borderColor={
                              !selected?.id ? 'red.500' : 'brand.primary'
                            }
                            h="50px"
                          />
                        </div>
                        {isOpen && list.length ? (
                          <div className="absolute top-[80px] bg-white w-full max-w-2xl rounded-2xl py-2 flex flex-col gap-4 left-0 ad-card-shadow z-10">
                            <div
                              className={`flex flex-col gap-4 max-h-[30vh] overflow-y-auto pb-5`}
                            >
                              {list
                                .filter(searchCategoriesFilter)
                                .map((item, index) => {
                                  return (
                                    <div
                                      className="cursor-pointer px-5 py-2 hover:bg-gray-100"
                                      onClick={() => {
                                        selectItem(item);
                                      }}
                                      key={item?.id}
                                    >
                                      {item?.title}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div className={`relative w-full`} ref={ref}>
                        <Text
                          mb={2}
                          fontWeight={500}
                          fontSize="16px"
                          color="brand.primary"
                          ref={expertCountryRef}
                        >
                          Location
                          {!selectedLocation?.id && (
                            <Text className="text-red-600 text-lg inline font-bold">
                              *
                            </Text>
                          )}
                        </Text>
                        <div
                          ref={locationRef}
                          className={`bg-white cursor-pointer flex justify-between items-center mb-2 max-w-2xl `}
                        >
                          <Flex className="flex-col gap-1 w-full">
                            <Input
                              key="location"
                              id="location"
                              value={searchLocationValue}
                              onChange={onChangeLocation}
                              placeholder={'Location'}
                              className="border border-[#134A70] w-full py-3 pr-2 pl-4 rounded-lg outline-none focus:outline-none focus-within:outline-none focus-visible:outline-none"
                              borderRadius="10px"
                              borderWidth={1}
                              borderColor={
                                !selectedLocation?.id
                                  ? 'red.500'
                                  : 'brand.primary'
                              }
                              h="50px"
                            />
                          </Flex>
                        </div>
                        {isLocationOpen && countriesList.length ? (
                          <div className="absolute top-[80px] bg-white w-full max-w-2xl rounded-2xl py-2 flex flex-col gap-4 left-0 ad-card-shadow z-10">
                            <div
                              className={`flex flex-col gap-4 max-h-[30vh] overflow-y-auto pb-5`}
                            >
                              {countriesList
                                .filter(searchLocationFilter)
                                .map((item, index) => {
                                  return (
                                    <div
                                      className="cursor-pointer px-5 py-2 hover:bg-gray-100"
                                      onClick={() => {
                                        selectLocation(item);
                                      }}
                                      key={item?.id}
                                    >
                                      {item?.title}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <Flex
                        className="w-full flex-wrap gap-5 my-5"
                        justifyContent="space-between"
                      >
                        <BrandButton
                          text="Submit"
                          width="100px"
                          colorScheme="blue"
                          isLoading={isLoading}
                          onClick={submitExpertData}
                        />
                        <BrandButton
                          text="Cancel"
                          width="100px"
                          onClick={closeEditExpert}
                        />
                      </Flex>
                    </Box>
                  )}
                </Flex>
              ) : null}
            </Box>
          )}
        </Stack>
      </Flex>
    </Box>
  );
};

export default AccountSettings;
